import React, { useState, useEffect } from 'react';
import AIIntelligencePanel from './AIIntelligencePanel';
import {
  TrendingUp, Users, DollarSign, Phone, Award, RefreshCw,
  CheckCircle, Target, Calendar, Trophy,
} from 'lucide-react';
import {
  Badge, emptyMsg, cardWrap, neuShadow, neuShadowSm,
  btnSm,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   SalesDashboardTab — at-a-glance metrics for sales leads + managers.
   • Today's lead intake, calls, weekly trend
   • Open opportunities count + pipeline value
   • Conversion % (lead → qualified) and Close % (won / decided)
   • Rep leaderboard ordered by $ won in window
   • Window selector (7d / 30d / 90d)
   ═══════════════════════════════════════════════════════════════════════════ */

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

export default function SalesDashboardTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load(d = days) {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.getSalesAnalytics({ days: d });
      setData(r);
    } catch (err) {
      console.error('Sales analytics failed', err);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line
  useEffect(() => { load(days); }, [days]);

  const kpis = [
    { label: 'Leads today',    value: data?.leadsToday ?? '—',                     color: '#62BB46', icon: Users },
    { label: 'Leads this week', value: data?.leadsThisWeek ?? '—',                  color: '#2E8B98', icon: TrendingUp },
    { label: 'Calls today',     value: data?.callsToday ?? '—',                     color: '#F26522', icon: Phone },
    { label: 'Open pipeline',   value: data ? fmtMoney(data.openPipelineValue) : '—', color: '#256F7A', icon: DollarSign,
      sub: data ? `${data.openOppsCount} opportunit${data.openOppsCount === 1 ? 'y' : 'ies'}` : null },
    { label: `Won (${days}d)`,  value: data ? fmtMoney(data.wonAmount) : '—',       color: '#15803D', icon: Trophy,
      sub: data ? `${data.wonCount} deal${data.wonCount === 1 ? '' : 's'}` : null },
    { label: 'Conversion %',    value: data ? `${data.conversionPct}%` : '—',       color: '#2E8B98', icon: Target,
      sub: 'leads moving past New' },
    { label: 'Close %',         value: data ? `${data.closePct}%` : '—',            color: '#B45309', icon: CheckCircle,
      sub: 'won / decided opps' },
    { label: 'Lost',            value: data?.lostCount ?? '—',                      color: '#DC3545', icon: Award,
      sub: `${days}-day window` },
  ];

  return (
    <>
      {/* Phase F.2: AI Intelligence panel — silent role gate */}
      <AIIntelligencePanel kind="sales" />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <TrendingUp size={18} color="#2E8B98" />
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>Sales Dashboard</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '6px 12px', borderRadius: 999, border: 'none',
              background: days === d ? '#2E8B98' : '#F1F5F9',
              color: days === d ? '#fff' : '#475569',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>{d}d</button>
          ))}
          <button onClick={() => load()} style={btnSm} title="Refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {kpis.map((k, i) => {
          const I = k.icon;
          return (
            <div key={i} style={{ background: '#F8F9FA', borderRadius: 14, boxShadow: neuShadow, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <I size={13} color={k.color} />
                <span style={{ fontSize: 10, color: '#6C757D', fontWeight: 700, textTransform: 'uppercase' }}>{k.label}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value}</div>
              {k.sub && <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2 }}>{k.sub}</div>}
            </div>
          );
        })}
      </div>

      {/* Rep leaderboard */}
      <div style={{ ...cardWrap, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <Trophy size={14} color="#B45309" />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>
            Rep Leaderboard <span style={{ color: '#6C757D', fontWeight: 400 }}>· last {days} days</span>
          </span>
        </div>
        {loading && !data ? (
          <div style={emptyMsg}>Loading…</div>
        ) : !data?.repLeaderboard?.length ? (
          <div style={emptyMsg}>No rep activity in the last {days} days.</div>
        ) : (
          <div className="responsive-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '32px 2fr 1fr 1fr 1fr',
              fontSize: 10, color: '#ADB5BD', fontWeight: 700, textTransform: 'uppercase',
              padding: '4px 8px',
              minWidth: 460,
            }}>
              <div>#</div>
              <div>Rep</div>
              <div>Open contacts</div>
              <div>Won</div>
              <div>$ Won</div>
            </div>
            {data.repLeaderboard.map((r, i) => (
              <div key={r.id} style={{
                display: 'grid', gridTemplateColumns: '32px 2fr 1fr 1fr 1fr',
                padding: '8px',
                background: i % 2 === 0 ? '#fff' : '#FAFBFC',
                borderRadius: 6,
                alignItems: 'center',
                fontSize: 12,
                minWidth: 460,
              }}>
                <div style={{ fontWeight: 800, color: i === 0 ? '#B45309' : i === 1 ? '#94A3B8' : i === 2 ? '#7C2D12' : '#475569' }}>
                  {i + 1}
                </div>
                <div style={{ fontWeight: 600, color: '#212529' }}>{r.display_name}</div>
                <div>{r.open_contacts}</div>
                <div>{r.won_count}</div>
                <div style={{ fontWeight: 700, color: '#15803D' }}>{fmtMoney(r.won_amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Helper note */}
      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 10, textAlign: 'center' }}>
        Conversion % = leads that moved past "New" stage. Close % = won opportunities ÷ (won + lost).
        Pipeline value = sum of `amount` on open opportunities.
      </div>
    </>
  );
}
